// VideosPage.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { BackToClassButton, ClassPageActionBar } from "./ClassPageActions";
import { createSavedVideo, deleteSavedVideo, importLegacySavedVideos, listSavedVideos, type SavedVideo, updateSavedVideo } from "./savedVideos";
import { isDemoSession } from "./api";
import { useUiLanguage } from "./i18n/UiLanguageContext";

type VideoItem = SavedVideo;

function extractYouTubeId(url: string): string | null {
  try {
    const u = new URL(url.trim());

    // youtu.be/<id>
    if (u.hostname.includes("youtu.be")) {
      const id = u.pathname.replace("/", "").trim();
      return id || null;
    }

    // youtube.com/watch?v=<id>
    if (u.hostname.includes("youtube.com")) {
      const v = u.searchParams.get("v");
      if (v) return v;

      // youtube.com/embed/<id>
      const parts = u.pathname.split("/").filter(Boolean);
      const embedIndex = parts.indexOf("embed");
      if (embedIndex >= 0 && parts[embedIndex + 1]) return parts[embedIndex + 1];

      // youtube.com/shorts/<id>
      const shortsIndex = parts.indexOf("shorts");
      if (shortsIndex >= 0 && parts[shortsIndex + 1]) return parts[shortsIndex + 1];
    }

    return null;
  } catch {
    return null;
  }
}

function thumbUrl(videoId: string) {
  return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
}

function embedUrl(videoId: string) {
  return `https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1`;
}

function MovieIcon({ className = "h-6 w-6" }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M4 7h16v12H4V7Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
      <path
        d="M4 7l4 4M8 7l4 4M12 7l4 4M16 7l4 4"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path d="M8 19v-4M16 19v-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

export default function VideosPage() {
  const { t } = useUiLanguage();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const classId = Number(id);

  const demoReadOnly = isDemoSession();

  const [items, setItems] = useState<VideoItem[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Add modal
  const [showAdd, setShowAdd] = useState(false);

  // Edit modal
  const [showEdit, setShowEdit] = useState(false);
  const [editing, setEditing] = useState<VideoItem | null>(null);

  // shared drafts for add/edit
  const [draftUrl, setDraftUrl] = useState("");
  const [draftTitle, setDraftTitle] = useState("");
  const [draftCategory, setDraftCategory] = useState("General");

  // Player modal
  const [playing, setPlaying] = useState<VideoItem | null>(null);

  // One authorised import path is shared with Whiteboard.
  useEffect(() => {
    let cancelled = false;
    // A public demo must never import browser-local videos into its fixed
    // fixture. Normal teacher accounts retain the established legacy import.
    const load = demoReadOnly ? listSavedVideos(classId) : importLegacySavedVideos(classId);
    void load.then((videos) => !cancelled && setItems(videos)).catch(() => !cancelled && setError("Could not load saved videos."));
    return () => { cancelled = true; };
  }, [classId, demoReadOnly]);

  function blockDemoMutation(action: string) {
    if (!demoReadOnly) return false;
    setError(null);
    window.dispatchEvent(new CustomEvent("elume-demo-restricted", {
      detail: {
        message: `This demo lets you explore a prepared class. In your own Elume account, you can ${action}. Start your free 14-day trial to try it yourself.`,
      },
    }));
    return true;
  }

  const grouped = useMemo(() => {
    const map = new Map<string, VideoItem[]>();
    for (const v of items) {
      const cat = (v.category || "General").trim() || "General";
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat)!.push(v);
    }
    const cats = Array.from(map.keys()).sort((a, b) => a.localeCompare(b));
    return cats.map((cat) => ({
      category: cat,
      videos: (map.get(cat) || []).sort((a, b) => Date.parse(b.added_at) - Date.parse(a.added_at)),
    }));
  }, [items]);

  function resetDraft() {
    setDraftUrl("");
    setDraftTitle("");
    setDraftCategory("General");
    setError(null);
  }

  function openAdd() {
    if (blockDemoMutation("add class videos")) return;
    resetDraft();
    setShowAdd(true);
  }

  function openEdit(v: VideoItem) {
    if (blockDemoMutation("edit class videos")) return;
    setError(null);
    setEditing(v);
    setDraftUrl(v.url);
    setDraftTitle(v.title);
    setDraftCategory(v.category || "General");
    setShowEdit(true);
  }

  async function addVideo() {
    if (blockDemoMutation("add class videos")) return;
    setError(null);

    const url = draftUrl.trim();
    if (!url) return setError("Paste a YouTube link first.");

    const ytId = extractYouTubeId(url);
    if (!ytId) return setError("That doesn’t look like a valid YouTube link.");

    const title = (draftTitle.trim() || "Untitled Video").trim();
    const category = (draftCategory.trim() || "General").trim();

    if (items.some((x) => x.youtube_id === ytId)) return setError("That video is already in your list.");
    try { const next = await createSavedVideo(classId, { youtube_id: ytId, url, title, category }); setItems((prev) => [next, ...prev]); setShowAdd(false); resetDraft(); }
    catch (caught: any) { setError(caught?.message || "Could not save that video."); }
  }

  async function saveEdit() {
    if (!editing) return;
    if (blockDemoMutation("edit class videos")) return;

    setError(null);

    const url = draftUrl.trim();
    if (!url) return setError("Paste a YouTube link first.");

    const ytId = extractYouTubeId(url);
    if (!ytId) return setError("That doesn’t look like a valid YouTube link.");

    if (ytId !== editing.youtube_id && items.some((x) => x.youtube_id === ytId)) {
      return setError("That video is already in your list.");
    }

    const title = (draftTitle.trim() || "Untitled Video").trim();
    const category = (draftCategory.trim() || "General").trim();

    try { const next = await updateSavedVideo(classId, editing.id, { youtube_id: ytId, url, title, category }); setItems((prev) => prev.map((v) => v.id === editing.id ? next : v)); setPlaying((p) => p && p.id === editing.id ? next : p); setShowEdit(false); setEditing(null); resetDraft(); }
    catch (caught: any) { setError(caught?.message || "Could not save that video."); }
  }

  async function deleteVideo(videoId: number) {
    if (blockDemoMutation("remove class videos")) return;
    try { await deleteSavedVideo(classId, videoId); } catch (caught: any) { setError(caught?.message || "Could not remove that video."); return; }
    setItems((prev) => prev.filter((v) => v.id !== videoId));
    if (playing?.id === videoId) setPlaying(null);
    if (editing?.id === videoId) {
      setShowEdit(false);
      setEditing(null);
      resetDraft();
    }
  }

  // Tailwind tokens (match your ELume vibe)
  const card =
    "rounded-3xl border-2 border-slate-200 bg-white shadow-[0_2px_0_rgba(15,23,42,0.06)]";
  const pill =
    "rounded-full border-2 border-slate-200 bg-white px-4 py-2 text-sm hover:bg-slate-50 active:translate-y-[1px]";
  const btnPrimary =
    "rounded-full border-2 border-emerald-700 bg-emerald-600 px-5 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50";

  return (
    <div className="min-h-screen bg-emerald-100 p-6">
      <div className="mx-auto max-w-7xl px-4 py-6">
        <ClassPageActionBar>
          <BackToClassButton classId={classId} />
        </ClassPageActionBar>

        {/* Header */}
        <div className={`${card} p-5 flex items-center justify-between gap-4`}>
          <div className="flex items-center gap-3">
            <span className="grid h-11 w-11 place-items-center rounded-2xl border-2 border-slate-200 bg-slate-50">
              <MovieIcon className="h-6 w-6" />
            </span>

            <div>
              <div className="text-2xl font-extrabold tracking-tight">{t("videos.title")}</div>
              <div className="text-sm text-slate-600">
                {t("videos.intro")}
              </div>
            </div>
          </div>

          {/* Top-right actions */}
          <div className="flex items-center gap-2">
            <button className={btnPrimary} type="button" onClick={openAdd}>
              + {t("videos.add")}
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="mt-6 space-y-6">
          {items.length === 0 ? (
            <div className={`${card} p-6 text-sm text-slate-700`}>
              {t("videos.empty")} <b>+ {t("videos.add")}</b> {t("videos.emptySuffix")}
            </div>
          ) : (
            grouped.map(({ category, videos }) => (
              <section key={category} className={`${card} p-5`}>
                <div className="flex items-center justify-between gap-3">
                  <h2 className="text-2xl font-extrabold">{category}</h2>
                  <div className="text-xs text-slate-500">
                    {videos.length} video{videos.length === 1 ? "" : "s"}
                  </div>
                </div>

                {/* ~12 thumbnails visible: 6 cols on large screens */}
                <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
                  {videos.map((v) => (
                    <div key={v.id} className="group">
                      <button
                        type="button"
                        onClick={() => setPlaying(v)}
                        className="w-full overflow-hidden rounded-2xl border-2 border-slate-200 bg-white hover:bg-slate-50"
                        title="Click to play"
                      >
                        <div className="relative">
                          <img
                            src={thumbUrl(v.youtube_id)}
                            alt={v.title}
                            className="h-28 w-full object-cover sm:h-32"
                            loading="lazy"
                          />

                          <div className="absolute inset-0 grid place-items-center">
                            <div className="rounded-full bg-black/55 px-3 py-2 text-white text-xs font-semibold opacity-0 group-hover:opacity-100 transition">
                              ▶ Play
                            </div>
                          </div>
                        </div>

                        <div className="p-2 text-left">
                          <div className="line-clamp-2 text-xs font-semibold text-slate-800">
                            {v.title}
                          </div>

                          <div className="mt-1 flex items-center justify-between gap-2">
                            <span className="text-[11px] text-slate-500">YouTube</span>

                            <div className="flex items-center gap-3">
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openEdit(v);
                                }}
                                className="text-[11px] font-semibold text-emerald-700 hover:underline"
                                title="Edit title/category/link"
                              >
                                Edit
                              </button>

                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  deleteVideo(v.id);
                                }}
                                className="text-[11px] font-semibold text-red-700 hover:underline"
                                title="Remove video"
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        </div>
                      </button>
                    </div>
                  ))}
                </div>
              </section>
            ))
          )}
        </div>
      </div>

      {/* Add Video Modal */}
      {showAdd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4">
          <div className="w-full max-w-xl rounded-2xl border-2 border-slate-200 bg-white p-5">
            <div className="text-xl font-semibold">{t("videos.addYoutube")}</div>
            <div className="mt-1 text-sm text-slate-600">
              {t("videos.addGuidance")}
            </div>

            <div className="mt-4 grid gap-3">
              <input
                className="w-full rounded-xl border-2 border-slate-200 bg-white px-3 py-2 text-sm"
                placeholder={t("videos.youtubeLink")}
                value={draftUrl}
                onChange={(e) => setDraftUrl(e.target.value)}
                autoFocus
              />

              <div className="grid gap-3 md:grid-cols-2">
                <input
                  className="w-full rounded-xl border-2 border-slate-200 bg-white px-3 py-2 text-sm"
                  placeholder={t("videos.videoTitle")}
                  value={draftTitle}
                  onChange={(e) => setDraftTitle(e.target.value)}
                />

                <input
                  className="w-full rounded-xl border-2 border-slate-200 bg-white px-3 py-2 text-sm"
                  placeholder={t("videos.category")}
                  value={draftCategory}
                  onChange={(e) => setDraftCategory(e.target.value)}
                />
              </div>

              {error && (
                <div className="rounded-xl border-2 border-red-200 bg-red-50 p-3 text-sm text-red-800">
                  {error}
                </div>
              )}

              <div className="mt-2 flex justify-end gap-2">
                <button
                  className={pill}
                  type="button"
                  onClick={() => {
                    setShowAdd(false);
                    resetDraft();
                  }}
                >
                  {t("common.cancel")}
                </button>
                <button className={btnPrimary} type="button" onClick={addVideo}>
                  {t("videos.add")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Video Modal */}
      {showEdit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4">
          <div className="w-full max-w-xl rounded-2xl border-2 border-slate-200 bg-white p-5">
            <div className="text-xl font-semibold">{t("videos.edit")}</div>
            <div className="mt-1 text-sm text-slate-600">
              {t("videos.editGuidance")}
            </div>

            <div className="mt-4 grid gap-3">
              <input
                className="w-full rounded-xl border-2 border-slate-200 bg-white px-3 py-2 text-sm"
                placeholder={t("videos.youtubeLink")}
                value={draftUrl}
                onChange={(e) => setDraftUrl(e.target.value)}
                autoFocus
              />

              <div className="grid gap-3 md:grid-cols-2">
                <input
                  className="w-full rounded-xl border-2 border-slate-200 bg-white px-3 py-2 text-sm"
                  placeholder={t("videos.videoTitle")}
                  value={draftTitle}
                  onChange={(e) => setDraftTitle(e.target.value)}
                />

                <input
                  className="w-full rounded-xl border-2 border-slate-200 bg-white px-3 py-2 text-sm"
                  placeholder={t("videos.category")}
                  value={draftCategory}
                  onChange={(e) => setDraftCategory(e.target.value)}
                />
              </div>

              {error && (
                <div className="rounded-xl border-2 border-red-200 bg-red-50 p-3 text-sm text-red-800">
                  {error}
                </div>
              )}

              <div className="mt-2 flex justify-end gap-2">
                <button
                  className={pill}
                  type="button"
                  onClick={() => {
                    setShowEdit(false);
                    setEditing(null);
                    resetDraft();
                  }}
                >
                  {t("common.cancel")}
                </button>
                <button className={btnPrimary} type="button" onClick={saveEdit}>
                  {t("common.save")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Player Modal */}
      {playing && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
          onClick={() => setPlaying(null)}
        >
          <div
            className="w-full max-w-4xl rounded-2xl border-2 border-slate-200 bg-white p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-lg font-semibold truncate">{playing.title}</div>
                <div className="text-sm text-slate-600 truncate">{playing.category}</div>
              </div>
              <button className={pill} type="button" onClick={() => setPlaying(null)}>
                Close
              </button>
            </div>

            <div className="mt-3 aspect-video w-full overflow-hidden rounded-xl border border-slate-200 bg-black">
              <iframe
                title={playing.title}
                src={embedUrl(playing.youtube_id)}
                className="h-full w-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>

            <div className="mt-3 flex items-center justify-between">
              <a
                href={playing.url}
                target="_blank"
                rel="noreferrer"
                className="text-sm font-semibold text-emerald-700 hover:underline"
              >
                Open on YouTube
              </a>

              <div className="flex items-center gap-3">
                <button
                  type="button"
                  className="text-sm font-semibold text-emerald-700 hover:underline"
                  onClick={() => {
                    openEdit(playing);
                    setPlaying(null);
                  }}
                >
                  Edit
                </button>

                <button
                  type="button"
                  className="text-sm font-semibold text-red-700 hover:underline"
                  onClick={() => deleteVideo(playing.id)}
                >
                  Remove
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
